<template>
    <div class="inventory-details">
        <div class="page-header">
            <div class="header-left">
                <button @click="goBack" class="btn-back">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                    Retour
                </button>
                <div class="header-info">
                    <h1 class="page-title">Détails de l'inventaire</h1>
                    <p v-if="inventory" class="inventory-ref">{{ inventory.reference_number }}</p>
                </div>
            </div>
            <div v-if="inventory" class="header-actions">
                <button v-if="canFinalizeInventory" @click="finalizeInventory" class="btn-finalize"
                    :disabled="isProcessing">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Finaliser l'inventaire
                </button>
                <!-- <span :class="['status-badge', `status-${inventory.status}`]">
                    {{ getStatusLabel(inventory.status) }}
                </span> -->
            </div>
        </div>

        <div v-if="loading" class="loading-container">
            <div class="spinner"></div>
            <p>Chargement des détails...</p>
        </div>

        <div v-else-if="error" class="error-container">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <p>{{ error }}</p>
            <button @click="loadInventory" class="btn-primary">Réessayer</button>
        </div>

        <div v-else-if="inventory" class="content-wrapper">
            <div class="summary-section">
                <div class="summary-card">
                    <div class="card-header">
                        <h2>Résumé de l'inventaire</h2>
                    </div>
                    <div class="card-body">
                        <div class="info-grid">
                            <div class="info-item">
                                <div class="info-label">Référence</div>
                                <div class="info-value">{{ inventory.reference_number }}</div>
                            </div>
                            <div class="info-item">
                                <div class="info-label">Statut</div>
                                <div class="info-value">
                                    <span :class="['status-badge', `status-${inventory.status}`]">
                                        {{ getStatusLabel(inventory.status) }}
                                    </span>
                                </div>
                            </div>
                            <div class="info-item">
                                <div class="info-label">Créé par</div>
                                <div class="info-value">{{ inventory.user?.name || 'N/A' }}</div>
                            </div>
                            <div class="info-item">
                                <div class="info-label">Date de création</div>
                                <div class="info-value">{{ formatDate(inventory.created_at) }}</div>
                            </div>
                            <div class="info-item">
                                <div class="info-label">Date de début</div>
                                <div class="info-value">{{ inventory.started_at ? formatDate(inventory.started_at) : '-'
                                    }}</div>
                            </div>
                            <div class="info-item">
                                <div class="info-label">Date de fin</div>
                                <div class="info-value">{{ inventory.completed_at ? formatDate(inventory.completed_at) :
                                    '-' }}</div>
                            </div>
                        </div>

                        <div class="stats-grid">
                            <div class="stat-card">
                                <div class="stat-icon total">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round">
                                        <path
                                            d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z">
                                        </path>
                                    </svg>
                                </div>
                                <div class="stat-info">
                                    <div class="stat-value">{{ inventory.total_items || 0 }}</div>
                                    <div class="stat-label">Produits total</div>
                                </div>
                            </div>
                            <div class="stat-card">
                                <div class="stat-icon counted">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                </div>
                                <div class="stat-info">
                                    <div class="stat-value">{{ inventory.counted_items || 0 }}</div>
                                    <div class="stat-label">Comptés</div>
                                </div>
                            </div>
                            <div class="stat-card">
                                <div class="stat-icon pending">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <polyline points="12 6 12 12 16 14"></polyline>
                                    </svg>
                                </div>
                                <div class="stat-info">
                                    <div class="stat-value">{{ inventory.pending_items || 0 }}</div>
                                    <div class="stat-label">En attente</div>
                                </div>
                            </div>
                            <div class="stat-card">
                                <div class="stat-icon progress">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <path d="M12 2a10 10 0 0 1 10 10"></path>
                                    </svg>
                                </div>
                                <div class="stat-info">
                                    <div class="stat-value">{{ inventory.progress_percentage || 0 }}%</div>
                                    <div class="stat-label">Progression</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="products-section">
                <div class="products-card">
                    <div class="card-header">
                        <h2>Produits de l'inventaire</h2>
                        <div class="header-filters">
                            <input v-model="searchQuery" type="text" placeholder="Rechercher un produit..."
                                class="search-input" />
                            <select v-model="filterStatus" class="filter-select">
                                <option value="">Tous les statuts</option>
                                <option value="pending">En attente</option>
                                <option value="counted">Comptés</option>
                                <option value="adjusted">Ajustés</option>
                                <option value="rejected">Rejetés</option>
                                <option value="approved">Approuvés</option>
                            </select>
                            <select v-model="filterDifference" class="filter-select">
                                <option value="">Toutes les différences</option>
                                <option value="match">Aucun écart</option>
                                <option value="surplus">Surplus</option>
                                <option value="deficit">Déficit</option>
                            </select>
                        </div>
                    </div>

                    <div v-if="selectedItems.length > 0" class="bulk-actions-bar">
                        <div class="bulk-info">
                            <span class="selected-count">{{ selectedItems.length }} produit(s) sélectionné(s)</span>
                        </div>
                        <div class="bulk-actions">
                            <button @click="bulkRecount" class="btn-action btn-secondary" :disabled="isProcessing">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <polyline points="23 4 23 10 17 10"></polyline>
                                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                                </svg>
                                Recompter
                            </button>
                            <button @click="bulkApplyQuantities" class="btn-action btn-primary"
                                :disabled="isProcessing || !canApplyQuantities">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                                Appliquer les quantités
                            </button>
                        </div>
                    </div>

                    <div class="table-container">
                        <table class="products-table">
                            <thead>
                                <tr>
                                    <th class="checkbox-cell">
                                        <input type="checkbox" :checked="isAllSelected" @change="toggleSelectAll"
                                            :disabled="filteredItems.length === 0" />
                                    </th>
                                    <th>Produit</th>
                                    <th>Catégorie</th>
                                    <th class="text-center">Théorique</th>
                                    <th class="text-center">Réel</th>
                                    <th class="text-center">Différence</th>
                                    <th class="text-center">Statut</th>
                                </tr>
                            </thead>
                            <tbody v-if="filteredItems.length > 0">
                                <tr v-for="item in filteredItems" :key="item.id" class="table-row">
                                    <td class="checkbox-cell">
                                        <input type="checkbox" :value="item.id" v-model="selectedItems" />
                                    </td>
                                    <td>
                                        <div class="product-cell">
                                            <div class="product-name">{{ item.product?.name }}</div>
                                            <div v-if="item.product?.sku" class="product-sku">
                                                SKU: {{ item.product.sku }}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span class="category-badge">
                                            {{ item.product?.category?.name || 'N/A' }}
                                        </span>
                                    </td>
                                    <td class="text-center">
                                        <span class="quantity expected">
                                            {{ item.expected_quantity }} {{ item.product?.unit || '' }}
                                        </span>
                                    </td>
                                    <td class="text-center">
                                        <span v-if="item.actual_quantity !== null" class="quantity actual">
                                            {{ item.actual_quantity }} {{ item.product?.unit || '' }}
                                        </span>
                                        <span v-else class="quantity-empty">-</span>
                                    </td>
                                    <td class="text-center">
                                        <span v-if="item.difference !== null"
                                            :class="['difference-badge', getDifferenceClass(item.difference)]">
                                            {{ item.difference > 0 ? '+' : '' }}{{ item.difference }}
                                        </span>
                                        <span v-else class="difference-empty">-</span>
                                    </td>
                                    <td class="text-center">
                                        <span :class="['item-status-badge', `status-${item.status}`]">
                                            {{ getItemStatusLabel(item.status) }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                            <tbody v-else>
                                <tr>
                                    <td colspan="7" class="empty-state">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48"
                                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"
                                            stroke-linecap="round" stroke-linejoin="round">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <line x1="12" y1="16" x2="12" y2="12"></line>
                                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                        </svg>
                                        <p>Aucun produit trouvé</p>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
            <div class="modal-container">
                <div class="modal-header">
                    <h3 class="modal-title">{{ modalData.title }}</h3>
                    <button @click="closeModal" class="modal-close">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>
                <div class="modal-body">
                    <div v-if="modalData.icon" :class="['modal-icon', modalData.iconType]">
                        <svg v-if="modalData.iconType === 'warning'" xmlns="http://www.w3.org/2000/svg" width="48"
                            height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <svg v-else-if="modalData.iconType === 'success'" xmlns="http://www.w3.org/2000/svg" width="48"
                            height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                    </div>
                    <p class="modal-message">{{ modalData.message }}</p>
                    <div v-if="modalData.details" class="modal-details">
                        <p v-for="(detail, index) in modalData.details" :key="index">{{ detail }}</p>
                    </div>
                </div>
                <div class="modal-footer">
                    <button @click="closeModal" class="modal-btn modal-btn-cancel">
                        {{ modalData.cancelText || 'Annuler' }}
                    </button>
                    <button @click="confirmModalAction"
                        :class="['modal-btn', 'modal-btn-confirm', modalData.confirmClass || '']"
                        :disabled="isProcessing">
                        <span v-if="isProcessing" class="btn-spinner"></span>
                        {{ modalData.confirmText || 'Confirmer' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import physicalInventoryService from '@/services/physicalInventoryService';
import { useToastStore } from '@/store/modules/toastStore';

export default {
    name: 'InventoryDetails',
    setup() {
        const toastStore = useToastStore();
        return { toastStore };
    },
    data() {
        return {
            loading: false,
            error: null,
            inventory: null,
            searchQuery: '',
            filterStatus: '',
            filterDifference: '',
            selectedItems: [],
            isProcessing: false,
            showModal: false,
            modalData: {
                title: '',
                message: '',
                details: null,
                icon: false,
                iconType: '',
                confirmText: '',
                cancelText: '',
                confirmClass: '',
                action: null
            }
        }
    },
    computed: {
        filteredItems() {
            if (!this.inventory || !this.inventory.items) return [];

            let items = [...this.inventory.items];

            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                items = items.filter(item => {
                    return item.product?.name?.toLowerCase().includes(query) ||
                        item.product?.sku?.toLowerCase().includes(query);
                });
            }

            if (this.filterStatus) {
                items = items.filter(item => item.status === this.filterStatus);
            }

            if (this.filterDifference) {
                items = items.filter(item => {
                    if (this.filterDifference === 'match') {
                        return item.difference === 0;
                    } else if (this.filterDifference === 'surplus') {
                        return item.difference > 0;
                    } else if (this.filterDifference === 'deficit') {
                        return item.difference < 0;
                    }
                    return true;
                });
            }

            return items;
        },
        isAllSelected() {
            return this.filteredItems.length > 0 &&
                this.selectedItems.length === this.filteredItems.length;
        },
        canApplyQuantities() {
            if (this.selectedItems.length === 0) return false;
            const selectedItemsData = this.inventory.items.filter(item =>
                this.selectedItems.includes(item.id)
            );
            return selectedItemsData.every(item =>
                item.status === 'counted' && item.actual_quantity !== null
            );
        },
        canFinalizeInventory() {
            if (!this.inventory || !this.inventory.items || this.inventory.items.length === 0) {
                return false;
            }

            // L'inventaire doit être en cours ou complété
            if (!['in_progress', 'completed'].includes(this.inventory.status)) {
                return false;
            }

            // Tous les items doivent être ajustés
            return this.inventory.items.every(item => item.status === 'adjusted');
        }
    },
    mounted() {
        this.loadInventory();
    },
    methods: {
        async loadInventory() {
            this.loading = true;
            this.error = null;

            try {
                const inventoryId = this.$route.params.id;
                this.inventory = await physicalInventoryService.getInventoryById(inventoryId);
            } catch (error) {
                console.error('Erreur lors du chargement:', error);
                this.error = 'Impossible de charger les détails de l\'inventaire';
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors du chargement des détails'
                });
            } finally {
                this.loading = false;
            }
        },

        goBack() {
            this.$router.push({ name: 'inventory-history' });
        },

        getStatusLabel(status) {
            return physicalInventoryService.getStatusLabel(status);
        },

        formatDate(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },

        getDifferenceClass(difference) {
            if (difference === 0) return 'match';
            if (difference > 0) return 'surplus';
            return 'deficit';
        },

        getItemStatusLabel(status) {
            const labels = {
                pending: 'En attente',
                counted: 'Compté',
                adjusted: 'Ajusté',
                rejected: 'Rejeté',
                approved: 'Approuvé'
            };
            return labels[status] || status;
        },

        toggleSelectAll() {
            if (this.isAllSelected) {
                this.selectedItems = [];
            } else {
                this.selectedItems = this.filteredItems.map(item => item.id);
            }
        },

        bulkRecount() {
            if (this.selectedItems.length === 0) return;

            this.modalData = {
                title: 'Recompter les produits',
                message: `Êtes-vous sûr de vouloir recompter ${this.selectedItems.length} produit(s) ?`,
                details: [
                    'Les produits seront remis en attente',
                    'L\'inventaire repassera en cours'
                ],
                icon: true,
                iconType: 'warning',
                confirmText: 'Recompter',
                cancelText: 'Annuler',
                confirmClass: 'btn-warning',
                action: this.executeRecount
            };
            this.showModal = true;
        },

        async executeRecount() {
            this.isProcessing = true;
            try {
                await physicalInventoryService.bulkRecount(
                    this.inventory.id,
                    this.selectedItems
                );

                this.closeModal();
                this.toastStore.addToast({
                    type: 'success',
                    message: `${this.selectedItems.length} produit(s) marqué(s) pour recomptage`
                });

                this.selectedItems = [];
                await this.loadInventory();
            } catch (error) {
                console.error('Erreur lors du recomptage:', error);
                this.closeModal();
                this.toastStore.addToast({
                    type: 'error',
                    message: error.response?.data?.message || 'Erreur lors du recomptage des produits'
                });
            } finally {
                this.isProcessing = false;
            }
        },

        bulkApplyQuantities() {
            if (this.selectedItems.length === 0 || !this.canApplyQuantities) return;

            const selectedItemsData = this.inventory.items.filter(item =>
                this.selectedItems.includes(item.id)
            );

            const itemsWithDifference = selectedItemsData.filter(item => item.difference !== 0);

            this.modalData = {
                title: 'Appliquer les quantités',
                message: `Appliquer les quantités inventoriées pour ${this.selectedItems.length} produit(s) ?`,
                details: [
                    `${itemsWithDifference.length} produit(s) ont des écarts qui seront ajustés`,
                    'Cette action créera des transactions d\'inventaire',
                    'Les stocks seront automatiquement ajustés'
                ],
                icon: true,
                iconType: 'warning',
                confirmText: 'Appliquer',
                cancelText: 'Annuler',
                confirmClass: 'btn-success',
                action: this.executeApplyQuantities
            };
            this.showModal = true;
        },

        async executeApplyQuantities() {
            this.isProcessing = true;
            try {
                await physicalInventoryService.bulkApplyInventoryAdjustments(
                    this.inventory.id,
                    this.selectedItems
                );

                this.closeModal();
                this.toastStore.addToast({
                    type: 'success',
                    message: `Quantités appliquées pour ${this.selectedItems.length} produit(s)`
                });

                this.selectedItems = [];
                await this.loadInventory();
            } catch (error) {
                console.error('Erreur lors de l\'application:', error);
                this.closeModal();
                this.toastStore.addToast({
                    type: 'error',
                    message: error.response?.data?.message || 'Erreur lors de l\'application des quantités'
                });
            } finally {
                this.isProcessing = false;
            }
        },

        finalizeInventory() {
            this.modalData = {
                title: 'Finaliser l\'inventaire',
                message: 'Êtes-vous sûr de vouloir finaliser cet inventaire ?',
                details: [
                    'Tous les produits ont été ajustés',
                    'L\'inventaire sera marqué comme terminé',
                    'Cette action est irréversible'
                ],
                icon: true,
                iconType: 'success',
                confirmText: 'Finaliser',
                cancelText: 'Annuler',
                confirmClass: 'btn-success',
                action: this.executeFinalizeInventory
            };
            this.showModal = true;
        },

        async executeFinalizeInventory() {
            this.isProcessing = true;
            try {
                await physicalInventoryService.completeInventory(this.inventory.id);

                this.closeModal();
                this.toastStore.addToast({
                    type: 'success',
                    message: 'Inventaire finalisé avec succès'
                });

                await this.loadInventory();
            } catch (error) {
                console.error('Erreur lors de la finalisation:', error);
                this.closeModal();
                this.toastStore.addToast({
                    type: 'error',
                    message: error.response?.data?.message || 'Erreur lors de la finalisation de l\'inventaire'
                });
            } finally {
                this.isProcessing = false;
            }
        },

        closeModal() {
            this.showModal = false;
            this.modalData = {
                title: '',
                message: '',
                details: null,
                icon: false,
                iconType: '',
                confirmText: '',
                cancelText: '',
                confirmClass: '',
                action: null
            };
        },

        confirmModalAction() {
            if (this.modalData.action && typeof this.modalData.action === 'function') {
                this.modalData.action();
            }
        }
    }
}
</script>

<style scoped>
.inventory-details {
    padding: 24px;
    max-width: 1400px;
    margin: 0 auto;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 32px;
    gap: 24px;
}

.header-left {
    display: flex;
    gap: 20px;
    align-items: center;
    flex: 1;
}

.btn-back {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border: 1px solid #e5e7eb;
    background: white;
    border-radius: 8px;
    color: #6b7280;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-back:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    color: #374151;
}

.header-info {
    flex: 1;
}

.page-title {
    font-size: 28px;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 4px;
}

.inventory-ref {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
}

.header-actions {
    display: flex;
    gap: 12px;
    align-items: center;
}

.btn-finalize {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #3FDA4D;
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.btn-finalize:hover:not(:disabled) {
    background: #35c441;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(63, 218, 77, 0.3);
}

.btn-finalize:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    opacity: 0.6;
}

.btn-finalize svg {
    flex-shrink: 0;
}

.status-badge {
    display: inline-block;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    text-transform: capitalize;
}

.status-draft {
    background: #f3f4f6;
    color: #6b7280;
}

.status-in_progress {
    background: #cffafe;
    color: #155e75;
}

.status-completed {
    background: #fef3c7;
    color: #92400e;
}

.status-approved {
    background: #dcfce7;
    color: #166534;
}

.status-rejected {
    background: #fee2e2;
    color: #991b1b;
}

.loading-container,
.error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 20px;
    gap: 16px;
}

.spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e2e8f0;
    border-top-color: #3FDA4D;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.error-container svg {
    color: #ef4444;
}

.error-container p {
    color: #6b7280;
    font-size: 16px;
}

.content-wrapper {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.summary-section,
.products-section {
    width: 100%;
}

.summary-card,
.products-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.card-header {
    padding: 20px 24px;
    border-bottom: 1px solid #e5e7eb;
    background: #f9fafb;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.card-header h2 {
    font-size: 18px;
    font-weight: 600;
    color: #1a202c;
    margin: 0;
}

.header-filters {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.search-input,
.filter-select {
    padding: 8px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 14px;
    transition: all 0.2s;
}

.search-input {
    min-width: 200px;
}

.search-input:focus,
.filter-select:focus {
    outline: none;
    border-color: #3FDA4D;
    background: #f9fafb;
}

.card-body {
    padding: 24px;
}

.info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 24px;
    margin-bottom: 32px;
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.info-label {
    font-size: 13px;
    color: #6b7280;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.info-value {
    font-size: 16px;
    color: #1a202c;
    font-weight: 600;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
}

.stat-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: #f9fafb;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
}

.stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.stat-icon.total {
    background: #cffafe;
    color: #155e75;
}

.stat-icon.counted {
    background: #dcfce7;
    color: #166534;
}

.stat-icon.pending {
    background: #fef3c7;
    color: #92400e;
}

.stat-icon.progress {
    background: #cffafe;
    color: #155e75;
}

.stat-info {
    flex: 1;
}

.stat-value {
    font-size: 24px;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 4px;
}

.stat-label {
    font-size: 13px;
    color: #6b7280;
}

.bulk-actions-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: linear-gradient(135deg, #0A5C6B 0%, #084651 100%);
    border-radius: 10px;
    margin-bottom: 16px;
    animation: slideDown 0.3s ease;
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.bulk-info {
    color: white;
}

.selected-count {
    font-size: 15px;
    font-weight: 600;
}

.bulk-actions {
    display: flex;
    gap: 12px;
}

.btn-action {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-action:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-action.btn-secondary {
    background: white;
    color: #6b7280;
}

.btn-action.btn-secondary:hover:not(:disabled) {
    background: #f9fafb;
    color: #374151;
}

.btn-action.btn-primary {
    background: #3FDA4D;
    color: white;
}

.btn-action.btn-primary:hover:not(:disabled) {
    background: #35c441;
}

.table-container {
    overflow-x: auto;
}

.products-table {
    width: 100%;
    border-collapse: collapse;
}

.checkbox-cell {
    width: 40px;
    text-align: center;
    padding: 12px !important;
}

.checkbox-cell input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #3FDA4D;
}

.products-table thead {
    background: #f9fafb;
}

.products-table th {
    padding: 14px 16px;
    text-align: left;
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #e5e7eb;
}

.products-table th.text-center {
    text-align: center;
}

.products-table tbody tr {
    border-bottom: 1px solid #e5e7eb;
    transition: background 0.2s;
}

.products-table tbody tr:hover {
    background: #f9fafb;
}

.products-table td {
    padding: 16px;
    font-size: 14px;
    color: #1a202c;
}

.products-table td.text-center {
    text-align: center;
}

.product-cell {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.product-name {
    font-weight: 600;
    color: #1a202c;
}

.product-sku {
    font-size: 12px;
    color: #6b7280;
}

.category-badge {
    display: inline-block;
    padding: 4px 10px;
    background: #f3f4f6;
    border-radius: 6px;
    font-size: 12px;
    color: #6b7280;
    font-weight: 500;
}

.quantity {
    font-weight: 600;
}

.quantity.expected {
    color: #6b7280;
}

.quantity.actual {
    color: #1a202c;
}

.quantity-empty,
.difference-empty {
    color: #d1d5db;
}

.difference-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 700;
    min-width: 50px;
}

.difference-badge.match {
    background: #dcfce7;
    color: #166534;
}

.difference-badge.surplus {
    background: #dcfce7;
    color: #166534;
}

.difference-badge.deficit {
    background: #fee2e2;
    color: #991b1b;
}

.item-status-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
}

.item-status-badge.status-pending {
    background: #fef3c7;
    color: #92400e;
}

.item-status-badge.status-counted {
    background: #dcfce7;
    color: #166534;
}

.item-status-badge.status-adjusted {
    background: #e0e7ff;
    color: #4338ca;
}

.item-status-badge.status-rejected {
    background: #fee2e2;
    color: #991b1b;
}

.item-status-badge.status-approved {
    background: #dcfce7;
    color: #166534;
}

.empty-state {
    text-align: center;
    padding: 60px 20px;
}

.empty-state svg {
    color: #d1d5db;
    margin-bottom: 16px;
}

.empty-state p {
    color: #9ca3af;
    font-size: 14px;
}

.btn-primary {
    padding: 10px 20px;
    background: #3FDA4D;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-primary:hover {
    background: #35c441;
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 20px;
    animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

.modal-container {
    background: white;
    border-radius: 16px;
    max-width: 500px;
    width: 100%;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    animation: slideUp 0.3s ease;
    max-height: 90vh;
    overflow-y: auto;
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 24px 16px;
    border-bottom: 1px solid #e5e7eb;
}

.modal-title {
    font-size: 20px;
    font-weight: 700;
    color: #1a202c;
    margin: 0;
}

.modal-close {
    background: transparent;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: #9ca3af;
    transition: all 0.2s;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.modal-close:hover {
    background: #f3f4f6;
    color: #6b7280;
}

.modal-body {
    padding: 24px;
    text-align: center;
}

.modal-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.modal-icon.warning {
    background: #fef3c7;
    color: #f59e0b;
}

.modal-icon.success {
    background: #dcfce7;
    color: #3FDA4D;
}

.modal-message {
    font-size: 16px;
    color: #1a202c;
    font-weight: 600;
    margin: 0 0 16px;
    line-height: 1.5;
}

.modal-details {
    background: #f9fafb;
    border-radius: 8px;
    padding: 16px;
    text-align: left;
}

.modal-details p {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
    padding: 8px 0;
    display: flex;
    align-items: flex-start;
    gap: 8px;
}

.modal-details p::before {
    content: "•";
    color: #3FDA4D;
    font-weight: bold;
    font-size: 16px;
}

.modal-footer {
    display: flex;
    gap: 12px;
    padding: 16px 24px 24px;
    justify-content: flex-end;
}

.modal-btn {
    padding: 12px 24px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 100px;
    justify-content: center;
}

.modal-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.modal-btn-cancel {
    background: #f3f4f6;
    color: #6b7280;
}

.modal-btn-cancel:hover:not(:disabled) {
    background: #e5e7eb;
    color: #374151;
}

.modal-btn-confirm {
    background: #3FDA4D;
    color: white;
}

.modal-btn-confirm:hover:not(:disabled) {
    background: #35c441;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(63, 218, 77, 0.3);
}

.modal-btn-confirm.btn-warning {
    background: #f59e0b;
}

.modal-btn-confirm.btn-warning:hover:not(:disabled) {
    background: #d97706;
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.modal-btn-confirm.btn-success {
    background: #3FDA4D;
}

.modal-btn-confirm.btn-success:hover:not(:disabled) {
    background: #35c441;
    box-shadow: 0 4px 12px rgba(63, 218, 77, 0.3);
}

.btn-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
}

@media (max-width: 768px) {
    .inventory-details {
        padding: 16px;
    }

    .page-header {
        flex-direction: column;
        gap: 16px;
    }

    .header-left {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }

    .page-title {
        font-size: 22px;
    }

    .info-grid {
        grid-template-columns: 1fr;
        gap: 16px;
    }

    .stats-grid {
        grid-template-columns: 1fr;
    }

    .header-filters {
        width: 100%;
    }

    .search-input,
    .filter-select {
        width: 100%;
    }

    .products-table {
        font-size: 13px;
    }

    .products-table th,
    .products-table td {
        padding: 12px 8px;
    }

    .modal-container {
        max-width: 100%;
        margin: 0 16px;
    }

    .modal-footer {
        flex-direction: column-reverse;
    }

    .modal-btn {
        width: 100%;
    }
}
</style>
